import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import {
  selectSearchCriteria,
  selectFechaDesde,
  selectFechaHasta,
  selectEstado,
  selectNegocio,
  selectOficina,
} from "./actions";
import { setPage, setTake } from "../Filtros/actions";
import { calculateDate } from "~libs/dateCalculator";
import Searcher from "~styled/searcher/searcher";
import { Typography } from "@material-ui/core";
import Selector, { type OptionKV } from "~styled/selector/Selector";
import Pagination from "~styled/pagination/Pagination";
import DateRangePicker from "~styled/dateRangePicker/DateRangePicker";
import { queueMessage } from "~shared/snackbarsProvider/actions";
import { paginationOptions } from "~constants/pagination";
import {
  estadosSolicitudEmisionFilter,
  estadosSolicitudEmisionOptions,
} from "./estados";
import {
  estadosSolicitudEmisionAdminFilter,
  estadosSolicitudEmisionAdminOptions,
} from "./estadosAdmin";
import { negociosFilter, negociosOptions } from "./negocios";

const useStyles = makeStyles((theme: any) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "flex-start",
    paddingBottom: 20,
    width: "100%",
  },
  filtersContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  filters: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    paddingBottom: "1%",
  },
  label: {
    fontSize: "10px",
    fontWeight: 500,
    lineHeight: "1.3",
    textAlign: "left",
    color: theme.colours.grey2,
    paddingLeft: 5,
    paddingBottom: 5,
  },
  dateSelectorAndTitle: {
    display: "flex",
    flexDirection: "column",
    minWidth: 250,
  },
  horizontalSpace: { paddingRight: "10px", minWidth: 250 },
  horizontalSpaceSearcher: { paddingRight: "10px", minWidth: 250 },
  textItemContainer: { display: "flex", flexDirection: "column" },
}));

type V = string | number;

interface Slice {
  isLoading: boolean;
  totalRegistros: number;

  take: V;
  page: number;
  fechaDesde: string | Date | null;
  fechaHasta: string | Date | null;
  searchCriteria: string;
  estado: V[];
  negocio: V;
  oficina: V;
  isAdminUser: boolean;

  oficinasOptions?: Record<string, string>;
  oficinasFilter?: OptionKV<V>[];
}

const useReduxSelector = () =>
  useSelector<any, Slice>((state) => ({
    isLoading: state.solicitudesEmisionReducer.isLoading,
    totalRegistros: state.solicitudesEmisionReducer.totalRegistros,
    oficinasOptions: state.solicitudesEmisionReducer.oficinasOptions,
    oficinasFilter: state.solicitudesEmisionReducer.oficinasFilter,

    take: state.solicitudesEmisionFiltrosReducer.take,
    page: state.solicitudesEmisionFiltrosReducer.page,
    fechaDesde: state.solicitudesEmisionFiltrosReducer.fechaDesde,
    fechaHasta: state.solicitudesEmisionFiltrosReducer.fechaHasta,
    searchCriteria: state.solicitudesEmisionFiltrosReducer.searchCriteria,
    estado: state.solicitudesEmisionFiltrosReducer.estado,
    negocio: state.solicitudesEmisionFiltrosReducer.negocio,
    oficina: state.solicitudesEmisionFiltrosReducer.oficina,

    isAdminUser: state.authenticatedViewReducer.isAdminUser,
  }));

const messageLoading = "Cargando su consulta previa" as const;

const Filtros: React.FC = () => {
  const {
    isLoading,
    take,
    fechaDesde,
    fechaHasta,
    searchCriteria,
    page,
    totalRegistros,
    estado,
    negocio,
    isAdminUser,
    oficinasOptions,
    oficinasFilter,
    oficina,
  } = useReduxSelector();

  const dispatch = useDispatch();
  const classes = useStyles();

  const handleChange = <T,>(
    event: { target: { value: T } },
    action: (v: T) => any
  ) => {
    dispatch(setPage(0));
    dispatch(action(event.target.value));
  };

  const handleChangeInterval = (value: number | null) => {
    dispatch(setPage(0));
    if (value == null) return;
    dispatch(selectFechaDesde(calculateDate(new Date(), value)));
    dispatch(selectFechaHasta(new Date()));
  };

  const handleChangeEstado = (value: V) => {
    dispatch(setPage(0));
    dispatch(selectEstado(value, isAdminUser));
  };

  const handleChangeNegocio = (value: V) => {
    dispatch(setPage(0));
    dispatch(selectNegocio(value));
  };

  const handleChangeOficina = (value: V) => {
    dispatch(setPage(0));
    dispatch(selectOficina(value));
  };

  const handleChangePage = (value: number) => {
    dispatch(setPage(value));
  };

  const estadosLabelMap = isAdminUser
    ? (estadosSolicitudEmisionAdminOptions as Record<string, string>)
    : (estadosSolicitudEmisionOptions as Record<string, string>);
  const estadoKey = Array.isArray(estado) ? estado.join() : "";
  const negocioLabelMap = negociosOptions as Record<string, string>;

  const paginationOptionsStr = React.useMemo(
    () =>
      paginationOptions.map((o) => ({
        label: String(o.label),
        value: o.value,
      })),
    []
  );

  return (
    <div className={classes.root}>
      <>
        <div className={classes.filters}>
          <div className={classes.textItemContainer}>
            <Typography variant="h3" className={classes.label}>
              BUSCAR
            </Typography>
            <div className={classes.horizontalSpaceSearcher}>
              <Searcher
                borderSquared
                value={searchCriteria}
                filter={searchCriteria}
                placeholder="Nombre titular, Dominio"
                onChange={(event) => handleChange(event, selectSearchCriteria)}
                onClear={() =>
                  handleChange({ target: { value: "" } }, selectSearchCriteria)
                }
              />
            </div>
          </div>

          <div className={classes.dateSelectorAndTitle}>
            <Typography variant="h3" className={classes.label}>
              FECHA
            </Typography>
            <div className={classes.horizontalSpace}>
              <DateRangePicker
                fechaDesde={fechaDesde}
                fechaHasta={fechaHasta}
                onChangeInterval={handleChangeInterval}
                onChangeStartDate={(value) =>
                  handleChange({ target: { value } }, selectFechaDesde)
                }
                onChangeEndDate={(value) =>
                  handleChange({ target: { value } }, selectFechaHasta)
                }
                onClickDisabled={() => {
                  dispatch(queueMessage(messageLoading));
                }}
                disabled={isLoading}
              />
            </div>
          </div>

          <div className={classes.textItemContainer}>
            <Typography variant="h3" className={classes.label}>
              ESTADO
            </Typography>
            <div className={classes.horizontalSpace}>
              <Selector<V>
                value={estadosLabelMap[estadoKey]}
                options={
                  isAdminUser
                    ? (estadosSolicitudEmisionAdminFilter as OptionKV<V>[])
                    : (estadosSolicitudEmisionFilter as OptionKV<V>[])
                }
                onChange={handleChangeEstado}
                onClickDisabled={() => dispatch(queueMessage(messageLoading))}
                disabled={isLoading}
              />
            </div>
          </div>
        </div>
      </>

      <>
        <div className={classes.filtersContainer}>
          <div className={classes.filters}>
            <div className={classes.textItemContainer}>
              <Typography variant="h3" className={classes.label}>
                NEGOCIO
              </Typography>
              <div className={classes.horizontalSpace}>
                <Selector<V>
                  value={negocioLabelMap[String(negocio)]}
                  options={negociosFilter as OptionKV<V>[]}
                  onChange={handleChangeNegocio}
                  onClickDisabled={() => dispatch(queueMessage(messageLoading))}
                  disabled={isLoading}
                />
              </div>
            </div>

            {isAdminUser && oficinasOptions && (
              <div className={classes.textItemContainer}>
                <Typography variant="h3" className={classes.label}>
                  OFICINA
                </Typography>
                <div className={classes.horizontalSpace}>
                  <Selector<V>
                    value={oficinasOptions[String(oficina)]}
                    options={(oficinasFilter ?? []) as OptionKV<V>[]}
                    onChange={handleChangeOficina}
                    onClickDisabled={() =>
                      dispatch(queueMessage(messageLoading))
                    }
                    disabled={isLoading}
                  />
                </div>
              </div>
            )}
          </div>

          {!isLoading && (
            <Pagination<V>
              rowsPerPage={take}
              onChangeRowsPerPage={(value) =>
                handleChange({ target: { value } }, setTake)
              }
              page={page}
              onChangePage={handleChangePage}
              totalRegistros={totalRegistros}
              options={paginationOptionsStr as OptionKV<V>[]}
            />
          )}
        </div>
      </>
    </div>
  );
};

export default Filtros;
